import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { RoleBadge } from "@/components/Navbar";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [region, setRegion] = useState(user?.region || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [saving, setSaving] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const initials = user?.name?.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  const save = async () => {
    if (!region) return;
    setSaving(true);
    try {
      const res = await api.put("/profile/region", { region });
      setUser(res.data);
      toast.success(`Region updated to ${region}`);
    } catch (e) {
      toast.error("Failed to update region");
    } finally {
      setSaving(false);
    }
  };

  const savePhone = async () => {
    if (!phone.trim()) return;
    setSavingPhone(true);
    try {
      const res = await api.put("/profile/phone", { phone });
      setUser(res.data);
      toast.success("Phone number saved");
    } catch (e) {
      toast.error("Failed to save phone");
    } finally {
      setSavingPhone(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-[Outfit] font-bold tracking-tight text-4xl sm:text-5xl">Profile</h1>
        <p className="text-base text-muted-foreground font-[Manrope] mt-2">
          Manage your account and operating region.
        </p>

        <div className="mt-8 border border-border bg-white rounded-md p-6" data-testid="profile-card">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16 border border-border">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-zinc-100 text-zinc-700 text-lg">{initials}</AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-3">
                <p className="font-[Outfit] font-semibold text-xl">{user?.name}</p>
                <RoleBadge role={user?.role} />
              </div>
              <p className="text-sm text-muted-foreground font-[Manrope]">{user?.email}</p>
            </div>
          </div>

          <div className="mt-8 border-t border-border pt-6">
            <label className="text-sm font-[Manrope] font-medium">Operating Region</label>
            <p className="text-sm text-muted-foreground font-[Manrope] mb-3">
              Determines your dashboard greeting and client data.
            </p>
            <div className="flex items-center gap-3 max-w-sm">
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger data-testid="region-select" className="bg-white">
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="Poland" data-testid="region-option-poland">Poland</SelectItem>
                  <SelectItem value="Kenya" data-testid="region-option-kenya">Kenya</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={save}
                disabled={saving || !region || region === user?.region}
                data-testid="save-region-btn"
                className="bg-[#193962] hover:bg-[#12294A] text-white rounded-md transition-all duration-200 hover:-translate-y-[1px]"
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>

          <div className="mt-6 border-t border-border pt-6">
            <label className="text-sm font-[Manrope] font-medium">Numer telefonu</label>
            <p className="text-sm text-muted-foreground font-[Manrope] mb-3">
              Powiadomienia WhatsApp są wyłączone — briefy i przypomnienia Wingmana wysyłamy na Twój e‑mail.
            </p>
            <div className="flex items-center gap-3 max-w-sm">
              <Input
                data-testid="phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+48 600 000 000"
                className="bg-white"
              />
              <Button
                onClick={savePhone}
                disabled={savingPhone || !phone.trim() || phone === user?.phone}
                data-testid="save-phone-btn"
                className="bg-[#193962] hover:bg-[#12294A] text-white rounded-md transition-all duration-200 hover:-translate-y-[1px]"
              >
                {savingPhone ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
